import { useState, type ReactNode } from 'react';
import { createFileRoute, Link, redirect, useCanGoBack, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Amount, Button, Chip, Panel, PanelHeader, Ref, cn } from '@promanage/ui';
import { ROLE_LABELS, displayName } from '@promanage/contracts/person-display';
import {
  REF_PREFIX,
  formatAddress,
  formatNzDate,
  formatNzDateOr,
  formatRef,
  formatShare,
  parseRef,
  streetLine,
} from '@promanage/core';
import { RecordNotFound } from '../components/RecordNotFound';
import { TopBar } from '../components/TopBar';
import { RelatedRecordRow } from '../components/RelatedRecordRow';
import { RecordSkeleton } from '../components/RecordSkeleton';
import { RecordIdentity } from '../components/RecordIdentity';
import { Icon } from '../components/Icon';
import { RecordTabs } from '../components/RecordTabs';
import { RecordCommunications } from '../features/inbox/RecordCommunications';
import { readTabSearch } from '../components/tabSearch';
import { PayeeAccount } from '../features/disbursements/PayeeAccount';
import { PaymentSchedule } from '../features/disbursements/PaymentSchedule';
import { OwnerDocuments } from '../features/reports/OwnerDocuments';
import { RecordFiles } from '../features/reports/RecordFiles';
import { RecordLetters } from '../features/correspondence/RecordLetters';
import { contactMethods } from '../features/people/detail';
import { ownerKindLabel } from '../features/property/shares';
import type { OwnerContact, OwnerShare, PropertyBalance } from '../features/property/types';
import { trpc } from '../lib/trpc';
import { LegacyCode } from '../features/legacyCodes/LegacyCode';

/**
 * One owner entity, addressed by its display ref: /owners/OW000003.
 *
 * An owner is an ENTITY, not a person — "Solomona Holdings Ltd", the "R & K Beauchamp Trust".
 * It holds the GST number, the properties and the trust ledger account; the humans behind it
 * attach by role and live under People. That shape decides this page: what it shows is what the
 * entity HOLDS, which is the mirror image of /properties/$ref showing who holds one property.
 *
 * Same ref contract as /properties/$ref and /people/$ref — `beforeLoad` normalises anything
 * ref-shaped to the canonical form, and the component answers each remaining wrong shape
 * distinctly, because a ref for another entity, a typo and a number this agency never issued are
 * three different mistakes.
 */
export const Route = createFileRoute('/owners/$ref')({
  /*
   * Which section is open, in the URL. Shared with the property, tenancy and person pages —
   * see `readTabSearch` for why it is its own import-free module.
   */
  validateSearch: readTabSearch,

  beforeLoad: ({ params, search }) => {
    const parsed = parseRef(params.ref);
    if (parsed?.entity === 'owner') {
      const canonical = formatRef('owner', parsed.sequence);
      // Loop-safe: formatRef of a parsed canonical ref is that same string.
      if (canonical !== params.ref) {
        // `search` is carried over: normalising the ref must not close the tab somebody linked to.
        throw redirect({ to: '/owners/$ref', params: { ref: canonical }, search, replace: true });
      }
    }
  },
  component: OwnerDetailPage,
});

function OwnerDetailPage() {
  const { ref } = Route.useParams();
  const parsed = parseRef(ref);

  if (!parsed) {
    return (
      <Unresolved title="Not a record reference">
        <code className="font-mono text-ink">{ref}</code> is not a record reference. Owners look
        like <code className="font-mono text-ink">OW000003</code> — two letters for the record type,
        then its number.
      </Unresolved>
    );
  }

  if (parsed.entity !== 'owner') {
    return (
      <Unresolved title={`${formatRef(parsed.entity, parsed.sequence)} is not an owner`}>
        {/* "an invoice", "an inspection" — the entity name is data, so the article is derived. */}
        That is {/^[aeiou]/.test(parsed.entity) ? 'an' : 'a'} {parsed.entity} reference. Owner
        references start with <code className="font-mono text-ink">{REF_PREFIX.owner}</code>.
      </Unresolved>
    );
  }

  return <Detail refSeq={parsed.sequence} />;
}

function Detail({ refSeq }: { refSeq: number }) {
  const { tab } = Route.useSearch();
  const owner = useQuery(trpc.property.owner.queryOptions({ refSeq }, { staleTime: 10_000 }));

  if (owner.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Owner" subtitle="Loading…" />
        <RecordSkeleton />
      </div>
    );
  }

  if (owner.error || !owner.data) {
    return (
      <Unresolved title={`No owner ${formatRef('owner', refSeq)}`}>
        Nothing in this agency uses that reference. It may belong to a different agency — references
        are numbered per agency, so the same code means different records elsewhere.
      </Unresolved>
    );
  }

  const record = owner.data;
  const current = record.ownerships.filter((share) => share.endsOn === null);
  const previous = record.ownerships.filter((share) => share.endsOn !== null);
  const balance = BigInt(record.balanceCents);
  const hasAccount = record.ledgerAccountId !== null;

  /*
   * The balance, divided by the house each line of it was about, keyed for the property rows.
   *
   * `propertyBalances` is `ledger_entries` grouped by `property_id` — the same rows as the total,
   * not a second measurement — so every bucket here adds back to `balance` exactly. Which is why
   * the card foots: moving the breakdown onto the property rows would otherwise leave a reader
   * adding up four houses and landing somewhere other than the figure beside them.
   */
  const byProperty = new Map(
    record.propertyBalances.flatMap((bucket) =>
      bucket.propertyId === null ? [] : [[bucket.propertyId, BigInt(bucket.balanceCents)] as const],
    ),
  );

  const holdsNow = new Set(current.map((share) => share.propertyId));

  /*
   * Money the property rows above cannot account for, kept rather than dropped.
   *
   * Two kinds, and both are real: money held against no single house (an opening balance taken on
   * before the properties were matched, owner funds received to cover a bill), and money still
   * held against a house they have SOLD — which is precisely the bucket a breakdown hung off the
   * current ownership list loses silently. Zero buckets are dropped here, because a sold house
   * that is square is finished business; a house held now is shown at zero, because the reader
   * asked about that house.
   */
  const elsewhere = record.propertyBalances.filter(
    (bucket) =>
      BigInt(bucket.balanceCents) !== 0n &&
      (bucket.propertyId === null || !holdsNow.has(bucket.propertyId)),
  );

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Owner"
        subtitle={`${formatRef('owner', record.refSeq)} · ${ownerKindLabel(record.kind)}`}
        actions={
          <>
            <HeaderActions />
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {/* ── Identity ───────────────────────────────────────────────────────── */}
        <RecordIdentity
          title={record.name}
          symbol={<Icon name="owners" className="size-6" />}
          status={
            <Chip tone={record.status === 'archived' ? 'neutral' : 'positive'}>
              {record.status === 'archived' ? 'Archived' : 'Active'}
            </Chip>
          }
          subtitle={ownerKindLabel(record.kind)}
          facts={[
            { label: 'Properties held', value: holdsNow.size },
            { label: 'GST number', value: record.gstNumber || 'Not GST registered' },
            { label: 'Legacy code', value: <LegacyCode entity="owner" id={record.id} /> },
          ]}
        />

        <RecordTabs from="/owners/$ref" current={tab ?? 'overview'} label="Owner sections" />

        {tab === 'communications' ? (
          <RecordCommunications record={{ type: 'owner', id: record.id }} />
        ) : tab === 'documents' ? (
          /*
           * FULL WIDTH, not dropped into the overview's side column. A document row carries a
           * file name, a size, a state and a list of addresses, and an email address in a
           * third-of-a-page column is a truncated email address — which is the one thing an
           * operator is reading it to check.
           */
          <div className="flex flex-col gap-4">
            <OwnerDocuments ownerId={record.id} refSeq={record.refSeq} />
            {/*
             * BELOW WHAT WE SENT, not above it. Both are on this tab because both answer "what
             * documents relate to this owner", but the statements are the ones somebody arrives
             * here looking for — an owner ringing about a figure is the common case, and an
             * operator checking whether the management agreement is on file is the rarer one.
             */}
            <RecordFiles subject={{ type: 'owner', id: record.id }} />
            <RecordLetters subject={{ type: 'owner', id: record.id }} />
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
              {/* ── What they hold, and what is held for it ─────────────────────── */}
              <Panel className="p-0">
                <PanelHeader
                  title="Properties"
                  subtitle={
                    hasAccount
                      ? 'Current holdings, ownership shares and trust balances'
                      : 'Current holdings and ownership shares'
                  }
                  className="px-5 pt-5 pb-2"
                  aside={
                    <span className="text-[0.75rem] text-ink-subtle tabular-nums">
                      {current.length}
                    </span>
                  }
                />

                {current.length === 0 ? (
                  <p className="px-5 pb-5 text-[0.8125rem] text-ink-muted">
                    {record.ownerships.length === 0
                      ? 'No properties linked yet. Add this owner through a property’s ownership details.'
                      : 'No current holdings. Previous properties are listed below.'}
                  </p>
                ) : (
                  <ul className="flex flex-col">
                    {current.map((share, index) => (
                      <ShareRow
                        key={share.id}
                        share={share}
                        /*
                         * The money once per HOUSE, not once per share row. Two current shares in
                         * the same property would otherwise print the same balance twice, and the
                         * card would foot to more than the agency holds.
                         */
                        heldCents={
                          !hasAccount ||
                          current.findIndex((other) => other.propertyId === share.propertyId) !==
                            index
                            ? null
                            : (byProperty.get(share.propertyId) ?? 0n)
                        }
                      />
                    ))}
                  </ul>
                )}

                {elsewhere.length > 0 ? <HeldElsewhere buckets={elsewhere} /> : null}

                {/*
                 * The foot that makes the breakdown checkable.
                 *
                 * The rows above are this owner's whole balance, sliced — so they add to the figure
                 * in Trust position beside this card, and a reader who adds them up is entitled to
                 * see that they do. Shown only when there is more than one row to add: with a single
                 * house the row IS the total, and repeating it reads as a second number.
                 */}
                {hasAccount && current.length + elsewhere.length > 1 ? (
                  <div className="flex items-baseline justify-between gap-3 border-t-2 border-rule px-5 py-3">
                    <span className="text-[0.8125rem] font-semibold">Held for this owner</span>
                    <Amount
                      value={balance}
                      currency
                      notation="accounting"
                      className="text-[0.875rem] font-bold"
                    />
                  </div>
                ) : null}
              </Panel>

              {/*
               * Kept reachable rather than dropped. A share that ended in March is what a statement
               * for March is divided by, and those get re-run — an owner who sold mid-year still
               * has a tax summary coming.
               */}
              {previous.length > 0 ? <PreviousHoldings shares={previous} /> : null}

              {/*
               * ── The humans ──────────────────────────────────────────────────
               *
               * UNDER THE PROPERTIES, in the wide column, because it now carries reach details and
               * an email address in a narrow column is a truncated email address. It reads in the
               * order the work happens: what this entity holds, then who to ring about it.
               */}
              <Panel className="p-0">
                <PanelHeader
                  title="Contacts"
                  subtitle="Contacts for statements and enquiries"
                  className="px-5 pt-5 pb-2"
                  aside={
                    <span className="text-[0.75rem] text-ink-subtle tabular-nums">
                      {record.contacts.length}
                    </span>
                  }
                />

                {record.contacts.length === 0 ? (
                  <p className="border-t border-rule px-5 py-4 text-[0.8125rem] text-ink-muted">
                    <strong className="block text-ink">No contacts attached</strong>
                    Link a person to this owner to receive statements and enquiries.
                  </p>
                ) : (
                  <ul className="flex flex-col">
                    {record.contacts.map((contact) => (
                      // One human can hold two roles on the same entity — trustee and signatory,
                      // routinely — so the person alone is not a key.
                      <ContactRow
                        key={`${contact.personId}-${contact.roleType}`}
                        contact={contact}
                      />
                    ))}
                  </ul>
                )}
              </Panel>
            </div>

            <div className="order-first flex flex-col gap-4 lg:order-none">
              {/* ── The trust position ─────────────────────────────────────────── */}
              <Panel className="p-0">
                <PanelHeader
                  title="Trust position"
                  subtitle="Their money, held by the agency"
                  className="px-5 pt-5 pb-2"
                />

                {record.ledgerAccountId === null ? (
                  <p className="border-t border-rule px-5 py-4 text-[0.8125rem] text-ink-muted">
                    No ledger account. Every owner created here opens one, so this is an entity that
                    arrived another way — an import, or a merge — and nothing can be receipted to it
                    until it has one.
                  </p>
                ) : (
                  <>
                    <div className="border-t border-rule px-5 py-4">
                      <Amount
                        value={balance}
                        currency
                        notation="accounting"
                        tone={balance > 0n ? 'positive' : 'neutral'}
                        className="text-[1.5rem] font-bold"
                      />
                      <p className="mt-1 text-[0.75rem] text-ink-subtle">
                        {balance > 0n
                          ? 'Held on their behalf, awaiting disbursement'
                          : balance === 0n
                            ? 'Nothing held — everything received has been paid out'
                            : 'Overdrawn, which the ledger refuses to create; this can only be an opening balance'}
                      </p>
                    </div>

                    {/*
                     * NOT BROKEN DOWN HERE ANY MORE. The same money, sliced by house, is on the
                     * PROPERTIES card to the left — on the rows the reader is already looking at,
                     * where a balance sits beside the address and the share it belongs to rather
                     * than in a second list of the same houses.
                     *
                     * The two still foot to each other, and that card prints its own total so it
                     * can be checked without carrying this figure across the page.
                     */}

                    {/*
                     * THE STATEMENT FIRST, and the ledger behind it.
                     *
                     * They answer different questions and the difference is money. The ledger's
                     * amount columns are what the TRANSACTION moved — a $600 rent receipt reads
                     * $600 there, for an owner whose own balance moved $544.80 after the fee. The
                     * statement is the owner's share of each of those, running to the figure above,
                     * and it is what gets sent to them. So it leads, and the transaction list says
                     * plainly what its own numbers are.
                     */}
                    <Link
                      to="/reports/statements"
                      search={{ owner: record.refSeq }}
                      className="flex items-center justify-between gap-2 border-t border-rule px-5 py-3 text-[0.8125rem] transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk"
                    >
                      <span>
                        <span className="block font-medium">Statement for this owner</span>
                        <span className="block text-[0.75rem] text-ink-subtle">
                          Their share, month by month — printable and downloadable
                        </span>
                      </span>
                      <Icon name="arrow-right" size={14} className="shrink-0 text-ink-subtle" />
                    </Link>

                    <Link
                      to="/financial/transactions"
                      search={{ account: record.ledgerAccountId }}
                      className="flex items-center justify-between gap-2 border-t border-rule px-5 py-3 text-[0.8125rem] transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk"
                    >
                      <span>
                        <span className="block font-medium">
                          Transactions touching this account
                        </span>
                        <span className="block text-[0.75rem] text-ink-subtle">
                          Each amount is what the whole transaction moved
                        </span>
                      </span>
                      <Icon name="arrow-right" size={14} className="shrink-0 text-ink-subtle" />
                    </Link>
                  </>
                )}
              </Panel>

              {/*
               * ── Where their money goes ──────────────────────────────────────
               *
               * Directly under the trust position, and that adjacency is the point: the panel above
               * says how much the agency holds for this owner, and this one says where it will be
               * sent. docs/SECURITY.md §5 — changing it needs a trust_admin, starts a 24-hour hold,
               * and writes to whoever is on record now.
               */}
              <PayeeAccount ownerId={record.id} ownerName={record.name} />

              {/*
               * WHERE the money goes, then WHEN it goes. The pair is one conversation — "pay me on
               * Fridays into the new account" — and splitting them across two screens is how an
               * agency ends up with a correct bank account nobody is paying into.
               */}
              <PaymentSchedule ownerId={record.id} ownerName={record.name} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Back, then the list.
 *
 * The two are not the same thing and both earn their place: "Back" returns to wherever you
 * came from — the list with your filters, sort and scroll intact, or another owner's record
 * if you arrived through a relationship — while "All owners" is a fixed destination that
 * resets those.
 *
 * Back is hidden, not disabled, when there is no in-app history: the ref is addressable, so
 * arriving by pasted URL in a fresh tab is a real entry path, and there a browser back would
 * throw the user out of the app entirely.
 */
function HeaderActions() {
  const router = useRouter();
  const canGoBack = useCanGoBack();

  return (
    <>
      {canGoBack ? (
        <Button variant="secondary" size="sm" onClick={() => router.history.back()}>
          <Icon name="arrow-right" size={14} className="rotate-180" />
          Back
        </Button>
      ) : null}
      <Link to="/owners">
        <Button variant="secondary" size="sm">
          All owners
        </Button>
      </Link>
    </>
  );
}

/**
 * Money the property rows cannot account for.
 *
 * TWO KINDS, BOTH REAL. Money held against no single house — an opening balance taken on before
 * the properties were matched, owner funds received to cover a bill, an adjustment posted against
 * the entity — and money still held against a house this owner has SOLD. The second is exactly
 * what a breakdown hung off the current ownership list drops silently, and it is somebody's money.
 *
 * A PROPERTY MAY BE NEGATIVE HERE WHILE THE OWNER IS NOT, and that is not a bug to hide behind a
 * clamp: invariant 3 protects one OWNER's money from another's, and says nothing about one house
 * of one owner being covered by the rest of their portfolio — which is ordinary, lawful, and what
 * has happened whenever a repair outran a quiet month's rent. So a deficit is drawn plainly, with
 * a word beside it rather than a colour alone.
 */
function HeldElsewhere({ buckets }: { buckets: PropertyBalance[] }) {
  return (
    <div className="border-t border-rule bg-surface-sunk/40 px-5 py-3">
      <p className="text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
        Also held, against nothing above
      </p>

      <ul className="mt-2 flex flex-col gap-2">
        {buckets.map((bucket) => {
          const cents = BigInt(bucket.balanceCents);

          return (
            <li key={bucket.propertyId ?? 'unattributed'} className="flex items-baseline gap-3">
              <span className="min-w-0 flex-1">
                {bucket.propertyId === null || bucket.propertyRefSeq === null ? (
                  <>
                    <span className="block truncate text-[0.8125rem]">Not against a property</span>
                    {/*
                     * A REAL ANSWER, not a gap — so it says what it is rather than "unallocated".
                     */}
                    <span className="block text-[0.6875rem] text-ink-subtle">
                      Money held against this owner rather than one of their houses
                    </span>
                  </>
                ) : (
                  <>
                    <Link
                      to="/properties/$ref"
                      params={{ ref: formatRef('property', bucket.propertyRefSeq) }}
                      className="block truncate text-[0.8125rem] hover:text-accent"
                    >
                      {bucket.label}
                    </Link>
                    <span className="block text-[0.6875rem] text-ink-subtle">
                      No longer held — the balance outlasted the management
                    </span>
                  </>
                )}
              </span>

              <span className="flex shrink-0 flex-col items-end">
                <Amount
                  value={cents}
                  currency
                  notation="accounting"
                  className="text-[0.8125rem] font-semibold"
                />
                {cents < 0n ? (
                  <span className="text-[0.6875rem] text-ink-subtle">
                    covered by their other houses
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * One human behind the entity, and what to reach them on.
 *
 * NOT ONE LINK FOR THE WHOLE ROW, which is the convention everywhere else on this page. It cannot
 * be: the row now carries `mailto:` and `tel:` links of its own, and an anchor inside an anchor is
 * invalid and behaves differently in every browser. So the NAME is the link to the person record
 * and each contact method is its own.
 */
function ContactRow({ contact }: { contact: OwnerContact }) {
  const methods = contactMethods(contact);

  return (
    <RelatedRecordRow
      identity={
        <Link to="/people/$ref" params={{ ref: formatRef('person', contact.personRefSeq) }}>
          {displayName(contact)}
        </Link>
      }
      reference={<Ref entity="person" sequence={contact.personRefSeq} />}
      badges={<Chip tone="accent">{ROLE_LABELS[contact.roleType]}</Chip>}
    >
      {methods.length === 0 ? (
        /*
         * SAID PLAINLY, not left blank. An owner whose only contact has neither an email address
         * nor a phone number is an owner whose statement cannot be sent, and that is a thing to
         * go and fix rather than a field that happens to be empty.
         */
        <p className="mt-1.5 text-[0.75rem] text-ink-subtle">
          No email or phone recorded. Add an email to receive statements.
        </p>
      ) : (
        <dl className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1">
          {methods.map((method) => (
            <div key={method.key} className="flex min-w-0 items-baseline gap-2">
              <dt className="shrink-0 text-[0.6875rem] font-semibold tracking-[0.06em] text-ink-subtle uppercase">
                {method.label}
              </dt>
              <dd className="min-w-0">
                <a
                  href={method.href}
                  title={method.value}
                  className={cn(
                    'block truncate text-[0.8125rem] hover:text-accent hover:underline',
                    method.key === 'email' ? '' : 'tabular-nums',
                  )}
                >
                  {method.value}
                </a>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </RelatedRecordRow>
  );
}

/** One property this entity holds a share in, with the money held against it. */
function ShareRow({ share, heldCents }: { share: OwnerShare; heldCents: bigint | null }) {
  return (
    <li className="border-t border-rule">
      <Link
        to="/properties/$ref"
        params={{ ref: formatRef('property', share.propertyRefSeq) }}
        className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3 transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunk"
      >
        <Ref entity="property" sequence={share.propertyRefSeq} />

        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.8125rem] font-medium">{streetLine(share)}</span>
          <span className="block truncate text-[0.75rem] text-ink-subtle">
            {formatAddress(share)}
          </span>
        </span>

        <span className="flex shrink-0 flex-col items-end">
          <span className="text-[0.875rem] font-semibold tabular-nums">
            {formatShare(share.shareBp)}
          </span>
          <span className="text-[0.6875rem] text-ink-subtle tabular-nums">
            since {formatNzDate(share.startsOn)}
          </span>
        </span>

        {/*
         * SHOWN AT ZERO, unlike a standalone breakdown, which drops empty buckets.
         *
         * The reader is looking at this house because they wanted to know about this house, and
         * "nothing is held for it" is the answer to that question — a gap where the figure should
         * be is not. Null is the different case: no ledger account at all, where the column would
         * be a fabricated zero rather than a measured one.
         */}
        {heldCents === null ? null : (
          <span className="flex w-32 shrink-0 flex-col items-end">
            <Amount
              value={heldCents}
              currency
              notation="accounting"
              className="text-[0.875rem] font-semibold"
            />
            <span className="text-right text-[0.6875rem] text-ink-subtle">
              {heldCents === 0n
                ? 'nothing held'
                : heldCents < 0n
                  ? 'covered by their other houses'
                  : 'held for them'}
            </span>
          </span>
        )}

        <Icon name="arrow-right" size={14} className="shrink-0 text-ink-subtle" />
      </Link>
    </li>
  );
}

/**
 * Shares that have ended, collapsed.
 *
 * Behind a disclosure rather than dropped: a statement for a past month divides that month the
 * way it was owned then, and an owner who sold in March still has that March.
 */
function PreviousHoldings({ shares }: { shares: OwnerShare[] }) {
  const [open, setOpen] = useState(false);

  return (
    <Panel className="p-0">
      <PanelHeader
        title="Previously held"
        subtitle={
          open
            ? 'Kept, so a statement for a past month divides it the way it was owned then'
            : `${shares.length} ${shares.length === 1 ? 'share' : 'shares'} ended`
        }
        className="px-5 pt-5 pb-2"
        aside={
          <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? 'Hide' : 'Show'}
          </Button>
        }
      />
      {open ? (
        <ul className="flex flex-col">
          {shares.map((share) => (
            <li
              key={share.id}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-rule px-5 py-2.5 text-[0.8125rem]"
            >
              <Ref entity="property" sequence={share.propertyRefSeq} />
              <span className="min-w-0 flex-1 truncate text-ink-muted">{streetLine(share)}</span>
              <span className="tabular-nums">{formatShare(share.shareBp)}</span>
              <span className="text-[0.75rem] text-ink-subtle tabular-nums">
                {formatNzDate(share.startsOn)} – {formatNzDateOr(share.endsOn, 'present')}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="pb-3" />
      )}
    </Panel>
  );
}

/** The URL did not resolve to an owner. Three messages rather than one 404 — see /people/$ref. */
function Unresolved({ title, children }: { title: string; children: ReactNode }) {
  return (
    <RecordNotFound
      record="Owner"
      title={title}
      back={{ label: 'Back to owners', link: { to: '/owners' } }}
    >
      {children}
    </RecordNotFound>
  );
}
